<template>
  <div class="s-timetable-grid">
    <table>
      <thead>
        <tr>
          <th class="s-timetable-grid__period-col">
            Period
          </th>
          <th v-for="day in days" :key="day">
            {{ dayLabels[day] }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="periodNumber in periodNumbers" :key="periodNumber">
          <td class="s-timetable-grid__period-col">
            {{ periodNumber }}
          </td>
          <td
            v-for="day in days"
            :key="day"
            class="s-timetable-grid__cell"
            :class="{ 's-timetable-grid__cell--filled': periodFor(day, periodNumber), 's-timetable-grid__cell--editable': editable }"
            @click="onCellClick(day, periodNumber)"
          >
            <template v-if="periodFor(day, periodNumber)">
              <div class="s-timetable-grid__subject">
                {{ periodFor(day, periodNumber).subjectName || periodFor(day, periodNumber).label || '—' }}
              </div>
              <div v-if="periodFor(day, periodNumber).teacherName" class="s-timetable-grid__teacher">
                {{ periodFor(day, periodNumber).teacherName }}
              </div>
              <div v-if="periodFor(day, periodNumber).startTime" class="s-timetable-grid__time">
                {{ periodFor(day, periodNumber).startTime }}–{{ periodFor(day, periodNumber).endTime }}
              </div>
            </template>
            <v-icon v-else-if="editable" small class="s-timetable-grid__add-icon">
              {{ $icons.mdiPlus }}
            </v-icon>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="editable" class="s-timetable-grid__add-row">
      <SBtn label="+ Add Period Row" text small @click="$emit('add-period-row')" />
    </div>
  </div>
</template>

<script>
import { computed } from '@/plugins/setup'

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
const DAY_LABELS = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat' }
const DEFAULT_PERIOD_COUNT = 8

export default {
  props: {
    weeklySchedule: { type: Object, default: () => ({}) },
    editable: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const days = DAYS
    const dayLabels = DAY_LABELS

    // Rows are the union of period numbers already used anywhere in the week, padded up to a
    // sensible default so a brand new timetable still has rows to click on. Period numbers are
    // meant to line up across days (period 3 is roughly the same slot every day), even when a
    // particular day has fewer periods.
    const periodNumbers = computed(() => {
      const seen = new Set()
      days.forEach(day => {
        (props.weeklySchedule[day] || []).forEach(period => seen.add(period.periodNumber))
      })
      const max = seen.size ? Math.max(...seen, DEFAULT_PERIOD_COUNT) : DEFAULT_PERIOD_COUNT
      const numbers = []
      for (let i = 1; i <= max; i += 1) numbers.push(i)

      return numbers
    })

    const periodFor = (day, periodNumber) => (props.weeklySchedule[day] || [])
      .find(period => period.periodNumber === periodNumber)

    const onCellClick = (day, periodNumber) => {
      if (!props.editable) return
      emit('cell-click', { day, periodNumber, period: periodFor(day, periodNumber) })
    }

    return { days, dayLabels, periodNumbers, periodFor, onCellClick }
  },
}
</script>

<style scoped>
.s-timetable-grid {
  overflow-x: auto;
}
.s-timetable-grid table {
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;
}
.s-timetable-grid th,
.s-timetable-grid td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.s-timetable-grid__period-col {
  width: 64px;
  text-align: center;
  font-weight: 600;
}
.s-timetable-grid__cell {
  min-width: 110px;
  height: 56px;
}
.s-timetable-grid__cell--editable {
  cursor: pointer;
}
.s-timetable-grid__cell--editable:hover {
  background: rgba(0, 0, 0, 0.03);
}
.s-timetable-grid__cell--filled {
  background: rgba(25, 118, 210, 0.06);
}
.s-timetable-grid__subject {
  font-weight: 600;
  font-size: 0.85rem;
}
.s-timetable-grid__teacher,
.s-timetable-grid__time {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}
.s-timetable-grid__add-icon {
  opacity: 0.35;
}
.s-timetable-grid__add-row {
  margin-top: 4px;
}
</style>
